import { load } from '@tauri-apps/plugin-store'

import { Result } from '@blade/std-lib'
import { UnknownError } from '@root/shared/core'

import type { Store, StoreOptions } from '@tauri-apps/plugin-store'

const stores = new Map<string, Store>()

const resolveStore = async (path: string, options?: StoreOptions) => {
  const cached = stores.get(path)
  if (cached) {
    return cached
  }
  const store = await load(path, options)
  stores.set(path, store)
  return store
}

export class TauriStore {
  constructor(
    private path: string,
    private options?: StoreOptions,
  ) {}

  public get = async <T>(key: string) => {
    return Result.tryPromise({
      try: async () => {
        const store = await resolveStore(this.path, this.options)
        return store.get<T>(key)
      },
      catch: (error) => UnknownError.fromUnknown(error),
    })
  }

  public has = async (key: string) => {
    return Result.tryPromise({
      try: async () => {
        const store = await resolveStore(this.path, this.options)
        return store.has(key)
      },
      catch: (error) => UnknownError.fromUnknown(error),
    })
  }

  public set = async (key: string, value: unknown) => {
    return Result.tryPromise({
      try: async () => {
        const store = await resolveStore(this.path, this.options)
        return store.set(key, value)
      },
      catch: (error) => UnknownError.fromUnknown(error),
    })
  }

  public delete = async (key: string) => {
    return Result.tryPromise({
      try: async () => {
        const store = await resolveStore(this.path, this.options)
        return store.delete(key)
      },
      catch: (error) => UnknownError.fromUnknown(error),
    })
  }

  public clear = async () => {
    return Result.tryPromise({
      try: async () => {
        const store = await resolveStore(this.path, this.options)
        return store.clear()
      },
      catch: (error) => UnknownError.fromUnknown(error),
    })
  }

  public reset = async () => {
    return Result.tryPromise({
      try: async () => {
        const store = await resolveStore(this.path, this.options)
        return store.reset()
      },
      catch: (error) => UnknownError.fromUnknown(error),
    })
  }

  public keys = async () => {
    return Result.tryPromise({
      try: async () => {
        const store = await resolveStore(this.path, this.options)
        return store.keys()
      },
      catch: (error) => UnknownError.fromUnknown(error),
    })
  }

  public values = async <T>() => {
    return Result.tryPromise({
      try: async () => {
        const store = await resolveStore(this.path, this.options)
        return store.values<T>()
      },
      catch: (error) => UnknownError.fromUnknown(error),
    })
  }

  public entries = async <T>() => {
    return Result.tryPromise({
      try: async () => {
        const store = await resolveStore(this.path, this.options)
        return store.entries<T>()
      },
      catch: (error) => UnknownError.fromUnknown(error),
    })
  }

  public length = async () => {
    return Result.tryPromise({
      try: async () => {
        const store = await resolveStore(this.path, this.options)
        return store.length()
      },
      catch: (error) => UnknownError.fromUnknown(error),
    })
  }

  public reload = async () => {
    return Result.tryPromise({
      try: async () => {
        const store = await resolveStore(this.path, this.options)
        return store.reload()
      },
      catch: (error) => UnknownError.fromUnknown(error),
    })
  }

  public save = async () => {
    return Result.tryPromise({
      try: async () => {
        const store = await resolveStore(this.path, this.options)
        return store.save()
      },
      catch: (error) => UnknownError.fromUnknown(error),
    })
  }

  public close = async () => {
    return Result.tryPromise({
      try: async () => {
        const store = await resolveStore(this.path, this.options)
        return store.close()
      },
      catch: (error) => UnknownError.fromUnknown(error),
    })
  }
}

const GLOBAL_STORE_PATH = 'global.json'
export const globalStore = new TauriStore(GLOBAL_STORE_PATH, {
  autoSave: true,
  defaults: {},
})
